import { z } from "zod";

/** Local puppet-size offsets, never changes the performer's stage mark. */
export const zChoreoAccent = z
  .object({
    x: z.number().finite().min(-0.25).max(0.25).optional(),
    y: z.number().finite().min(-0.25).max(0.25).optional(),
    rotate: z.number().finite().min(-45).max(45).optional(),
    scale: z.number().finite().min(0.5).max(1.5).optional(),
    strength: z.number().finite().min(0).max(2).optional(),
    attack: z.enum(["snap", "ease", "recoil"]).optional(),
    /** Freeze the groove and accent at rest for this entire step. */
    hold: z.boolean().optional(),
  })
  .strict();
export type ChoreoAccent = z.infer<typeof zChoreoAccent>;
export interface ChoreoAccentMotion {
  accent: ChoreoAccent;
  startedAtMs: number;
  durationMs: number;
}

/** Pure clock sampling: no accumulated deltas, drift, or independent animation timers. */
export function choreoAccentTransform(accent: ChoreoAccent | undefined, progress: number) {
  const identity = { x: 0, y: 0, rotate: 0, scale: 1 };
  if (!accent || accent.hold || !Number.isFinite(progress) || progress <= 0 || progress >= 1) return identity;
  const t = Math.max(0, Math.min(1, progress));
  const smooth = (v: number) => v * v * (3 - 2 * v);
  let amount: number;
  if (accent.attack === "ease") amount = Math.sin(Math.PI * t);
  else if (t < 0.1) amount = smooth(t / 0.1);
  else if (accent.attack === "recoil") {
    const u = (t - 0.1) / 0.9;
    amount = Math.cos(u * Math.PI * 2) * (1 - u) ** 3;
  } else amount = 1 - smooth((t - 0.1) / 0.9);
  amount *= accent.strength ?? 1;
  return {
    x: (accent.x ?? 0) * amount,
    y: (accent.y ?? 0) * amount,
    rotate: (accent.rotate ?? 0) * amount,
    scale: 1 + ((accent.scale ?? 1) - 1) * amount,
  };
}
